import { useCallback, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';

// Only the last few turns are ever sent - the backend itself caps to the
// last 6 anyway (see server/help-bot-service.js), this is just a payload-
// size sanity bound on the client side, not a second source of truth.
const MAX_HISTORY_TURNS = 10;

const GENERIC_ERROR = 'Sorry, something went wrong. Please try again or contact support.';

/**
 * All Help Bot conversation state and API calls, no UI. The backend is
 * stateless (no server-side conversation storage - see
 * server/help-bot-service.js), so conversation history lives here and is
 * resent with every request.
 */
export function useHelpBot() {
  const { authedFetch } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const pendingRef = useRef(null);

  const open = useCallback(() => {
    setIsOpen(true);
    setIsMinimized(false);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);
  const minimize = useCallback(() => setIsMinimized(true), []);
  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
    setIsMinimized(false);
  }, []);

  // Shared core: calls the API for `text` and applies the result. Doesn't
  // touch the visible user-message bubble - callers decide whether one
  // needs adding (a fresh send does, a retry of an already-shown message
  // doesn't).
  const callApi = useCallback(
    async (text) => {
      setError(null);
      setIsLoading(true);
      try {
        const history = messagesRef.current
          .slice(-MAX_HISTORY_TURNS)
          .map((m) => ({ role: m.role, content: m.content }));
        const res = await authedFetch(`${API_BASE}/help-bot/chat`, {
          method: 'POST',
          body: JSON.stringify({ message: text, history }),
        });
        const data = await res.json().catch(() => null);

        if (!res.ok || !data?.success) {
          pendingRef.current = text;
          setError(data?.error || GENERIC_ERROR);
          return;
        }

        pendingRef.current = null;
        setMessages((prev) => [...prev, { role: 'assistant', content: data.message, actions: data.actions || [] }]);
      } catch {
        pendingRef.current = text;
        setError(GENERIC_ERROR);
      } finally {
        setIsLoading(false);
      }
    },
    [authedFetch],
  );

  const sendMessage = useCallback(
    (text) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) {
        return;
      }
      setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
      callApi(trimmed);
    },
    [callApi, isLoading],
  );

  const retry = useCallback(() => {
    if (!pendingRef.current || isLoading) {
      return;
    }
    callApi(pendingRef.current);
  }, [callApi, isLoading]);

  const newConversation = useCallback(() => {
    setMessages([]);
    setError(null);
    pendingRef.current = null;
  }, []);

  return {
    isOpen,
    isMinimized,
    messages,
    isLoading,
    error,
    open,
    close,
    minimize,
    toggle,
    sendMessage,
    retry,
    newConversation,
  };
}
